import type { Scene } from '../types'

export const teamScenes: Scene[] = [
  {
    id: 'TEAM-01',
    chapter: 'team',
    route: '/team/leadership',
    title: 'Merlion IT Leadership',
    headline: 'Behind every Merlion IT engagement stands the company\'s leadership.',
    supporting:
      'Merlion IT\'s corporate leadership sets the company\'s direction and supports all of its projects with conceptual, technical, advisory and supplementary expertise. It stands behind the named LAMS team; it does not replace or add to it.',
    visual: 'leadership-grid',
    whyItMatters:
      'The District contracts a named team, but it also relies on the company behind that team. This is the leadership responsible for Merlion IT\'s direction, financial stewardship, architecture and research — the support structure the LAMS team draws on throughout the project.',
    sourceRefs: [
      { kind: 'company', label: 'Merlion IT corporate leadership profile' },
      { kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Section 7.2; Appendix D' },
    ],
    presenterNotes: [
      'The RFP asked for the staff assigned to the project, and the proposal answers that with five named professionals. This section is about something different: the company behind them.',
      'Say it explicitly: this leadership supports every Merlion IT engagement — conceptually, technically, as advisers or as supplementary capacity. They are not additional key personnel, and nothing about the proposed team, scope or price changes.',
      'Jesse is the one person in both groups: part of Merlion IT\'s leadership, and the named Software Architect / Technical Lead on LAMS.',
      'One line per person here. Each has a scene of their own next.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'How corporate leadership relates to the project team',
        bullets: [
          'The named key personnel in the submitted proposal are unchanged: Felipe Nonato Marinho, Jesse Rovira, Pedro Bucci, Carlos Ribeiro and Bruno Freitas.',
          'Corporate leadership supports all Merlion IT engagements. Its contribution is company-level direction and support — conceptual, technical, advisory or supplementary — and not a separate delivery commitment under this proposal.',
          'Jesse Rovira belongs to both groups: Founder & Technical Leader of Merlion IT, and Software Architect / Technical Lead assigned to LAMS for the full project.',
          'Renato dos Santos Pereira signed the submitted proposal documentation as CEO on behalf of Merlion IT LLC.',
        ],
        sources: [
          { kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Section 7.2; Appendix D' },
          { kind: 'company', label: 'Merlion IT corporate leadership profile' },
        ],
      },
    ],
  },

  {
    id: 'TEAM-02',
    chapter: 'team',
    route: '/team/jesse-rovira',
    title: 'Jesse Rovira — Founder & Software Architect',
    headline: 'LAMS is architected by the founder of Merlion IT.',
    supporting:
      'Jesse Rovira founded Merlion IT and is the Software Architect and Technical Lead assigned to LAMS for the life of the project — more than twenty years of solution architecture for national-scale public-sector platforms.',
    visual: 'architect-profile',
    whyItMatters:
      'Architectural accountability for LAMS sits with the founder of the proposing company, named for the whole project rather than delegated below it. The person who answers for the design is the person who made it.',
    sourceRefs: [
      { kind: 'cv', label: 'Submitted CV — Jesse Rovira' },
      { kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Sections 7.2, 7.3' },
    ],
    presenterNotes: [
      'Jesse is the link between the two groups in this presentation: part of Merlion IT\'s leadership as founder, and the named Software Architect / Technical Lead on LAMS for the full project.',
      'Three strands, one sentence each: national-scale public platforms (PGA for CNA and MAPA, across Brazil\'s 27 federative units); Enterprise Asset Management (ID2 Tecnologia, IBM Maximo, the IBM Innovation award); and product engineering with applied AI.',
      'Keep the EAM strand short — it returns at the end of the project-team section, as the bridge into the concept.',
      'Do not state company age or imply a long corporate history. The claim is the architect\'s experience, not the tenure of the entity.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: ['Staff Qualifications — assigned Project Manager and staff'],
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Jesse Rovira — career record relevant to LAMS',
        table: {
          head: ['Organization', 'Role', 'Relevance to LAMS'],
          rows: [
            ['Merlion IT', 'Founder & Technical Leader', 'Architect and Technical Manager for the Agricultural Management Platform (PGA) for CNA in collaboration with MAPA; leadership of the animal and plant traceability systems at Instituto CNA; architectural design of Brazil\'s new agrochemical registration system, integrating three independent regulatory bodies into one platform; Izzy ERP / Construction / CRM product family for the U.S. market, with AI applied across the products.'],
            ['Fiocruz — SES-DF', 'Consultant, Digital Transformation in Public Health', 'Solution architect for public-health and government transformation initiatives: application, data and interoperability architecture; modular, extensible platforms designed to evolve with minimal source-code maintenance.'],
            ['Magna Sistemas', 'Area Manager & Contract Manager', 'Structured and expanded the IBM Maximo solutions practice after the acquisition of ID2 Tecnologia\'s project portfolio; solutions architect and director for the SERPRO Service Desk implementation on SmartCloud Control Desk.'],
            ['ID2 Tecnologia', 'Founder & Director', 'Founded on the concept that "Everything is an Asset," using IBM Maximo; projects for the Ministry of Tourism, DATASUS and DNIT; IBM "Innovation" award in 2010; conceived and developed PGA for MAPA.'],
            ['Flauto Consultores', 'Solutions Architect & Software Factory Leader', 'Structured and led a new software-factory division: development processes, technical standards and delivery methodology.'],
          ],
        },
        sources: [{ kind: 'cv', label: 'Submitted CV — Jesse Rovira' }],
      },
      {
        title: 'Role on LAMS',
        bullets: [
          'Software Architect / Technical Lead, project-long.',
          'Primary responsibilities: solution architecture, technical leadership, application and data architecture, integration strategy, security architecture, AI architecture, technical review and release quality.',
          'More than 20 years in software development, solution architecture, enterprise integration, workflow systems, data architecture and public-sector technology.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Sections 7.2, 7.3' }],
      },
    ],
  },

  {
    id: 'TEAM-03',
    chapter: 'team',
    route: '/team/renato-pereira',
    title: 'Renato dos Santos Pereira — CEO',
    headline: 'Executive leadership that connects business objectives to working systems.',
    supporting:
      'Chief Executive Officer of Merlion IT. More than 25 years in business management, team leadership, automation and business development, across operations in Brazil, the United States and China.',
    visual: 'profile-renato',
    whyItMatters:
      'Software projects fail more often at the boundary between business and technology than inside the code. Renato\'s contribution sits on that boundary: translating operational objectives into business requirements and keeping people, processes and systems aligned.',
    sourceRefs: [
      { kind: 'company', label: 'Merlion IT profile — Renato dos Santos Pereira' },
      { kind: 'proposal', label: 'Merlion Proposal', locator: 'Appendix D — signed as CEO' },
    ],
    presenterNotes: [
      'Renato leads Merlion IT as CEO, and signed the submitted proposal documentation on the company\'s behalf.',
      'Give the scale of leadership in one sentence: a structure of 80 technical and 20 administrative professionals at Atlas Copco, and the founding and growth of companies in the United States.',
      'Connect to Merlion IT directly: software, automation and AI initiatives for U.S. companies, including representing Merlion IT on the IzzyParcel real-estate platform.',
      'Keep the framing at company level — executive support for every engagement, not a LAMS delivery role.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Current roles',
        bullets: [
          'Chief Executive Officer, Merlion IT.',
          'Director of Complex Systems Architecture, Instituto Êxito.',
          'Chief Visionary Officer (CVO), Singulys Holding.',
          'CEO and co-founder, 2Easy Insurance.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Renato dos Santos Pereira' }],
      },
      {
        title: 'Technology and process transformation',
        bullets: [
          'At Merlion IT, software, automation and artificial-intelligence initiatives for companies in the U.S. market, including representing the company on IzzyParcel, a platform for the real-estate sector.',
          'At 2Easy Insurance, operational modernization across CRM, systems integration, commercial automation, multichannel service and AI — structuring the flow from opportunity capture and qualification to specialized service and customer relationship.',
          'Hands-on project repertoire with Zoho One, n8n and Twilio for process integration and service-journey automation, focused on business vision, operational flow definition and alignment of people, processes and systems.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Renato dos Santos Pereira' }],
      },
      {
        title: 'Corporate career and leadership',
        bullets: [
          'Atlas Copco, 1998–2010: technical operations, commercial management and team leadership, heading a structure of 80 technical and 20 administrative professionals. Recognized as “Best Salesperson Worldwide” in 2006, as recorded in the professional history.',
          'President and co-founder of TV Connect USA, leading its structuring and expansion over approximately 18 months, from the initial phase through market consolidation and the subsequent sale of the Singulys Group\'s stake.',
          'International business with RL4 Comercial Importadora, including supplier and partner relationships and cross-market operations. Business experience across more than 50 countries, including dozens of business trips to China.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Renato dos Santos Pereira' }],
      },
      {
        title: 'Education and professional development',
        bullets: [
          'Bachelor of Business Administration and Ethics — Cela International University, 2023.',
          'Business Administration with emphasis in Marketing — Universidade Paulista.',
          'Business Strategy with Artificial Intelligence — Massachusetts Institute of Technology (MIT).',
          'Electronic Process Controllers — SENAI, 1998.',
          'Microsoft Project — Stefanini, 2008.',
          'Excellence in Selling Services — Atlas Copco / Mercuri, 2004; Coaching Sales Performance — Mercuri, 2007; Excellence in People Management — Atlas Copco / MISAB, 2007.',
          'Complementary training in Master Mind and Public Speaking (Napoleon Hill) and in Coaching, Leadership and High Performance (FEBRACIS).',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Renato dos Santos Pereira' }],
      },
    ],
  },

  {
    id: 'TEAM-04',
    chapter: 'team',
    route: '/team/marcos-mazieri',
    title: 'Marcos Mazieri — CFO',
    headline: 'Financial stewardship grounded in research on project risk and cost.',
    supporting:
      'Chief Financial Officer of Merlion IT. Ph.D. in Administration with a focus on Innovation, business investor, and a researcher whose own work covers budget deviation, project risk and cost control under fixed-price contracts.',
    visual: 'profile-marcos',
    whyItMatters:
      'LAMS is offered at a not-to-exceed price, with payments tied to milestones and financial consequences for missed commitments. Merlion IT\'s financial leadership has studied those exact mechanisms — cost deviation, project risk and fixed-price delivery — as research, not only as practice.',
    sourceRefs: [
      { kind: 'company', label: 'Merlion IT profile — Marcos Mazieri' },
    ],
    presenterNotes: [
      'Position Marcos twice. In the market: a business investor who helps companies build, scale and become capital-market ready. In academia: innovation research with 46 publications and 526 citations.',
      'Make the relevance specific. His own research includes budget deviation in infrastructure projects, the effect of project risk management on project success, and cost maintenance in ERP implementations under fixed-price contracts — the same concerns behind a not-to-exceed, milestone-based offer.',
      'Keep his own publications separate from the theses he supervised. Both are listed in Explore Details; do not blur them when speaking.',
      'Company-level support, not LAMS key personnel.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-STAFF', 'EVAL-TC'],
    exploreDetails: [
      {
        title: 'Market activity',
        bullets: [
          'Chief Financial Officer, Merlion IT.',
          'Helps founders and companies design scalable businesses, accelerate growth, and prepare for institutional capital, M&A and IPO opportunities.',
          'Co-Founder and Business Investor, Houser — a proptech platform connecting homeowners with vetted contractors through AI-powered pricing, a curated contractor network and secure payment processing. Featured by Bloomberg Línea, December 2025.',
          'Infobankfor — an AI-by-design platform for strategy and finance analytics, drawing on innovation-management research.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Marcos Mazieri' }],
      },
      {
        title: 'Academic record',
        bullets: [
          'Ph.D. in Administration with a focus on Innovation, Universidade Nove de Julho (UNINOVE), where he served as Professor in the Post-Graduation Program in Business Management.',
          '46 publications, 49 conference papers, 2 book chapters and 3 patents; 526 citations, h-index 11.',
          'Published in journals including the European Business Review, World Patent Information, the Journal of Urban Technology and the International Journal of Project Management.',
          'Examiner, advisor or co-supervisor on 65 doctoral and master\'s thesis evaluation boards at UNINOVE (2016–2019); 20 theses supervised.',
          'International research collaboration including the Université de Toulon (France) and FEARP/USP.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Marcos Mazieri' }],
      },
      {
        title: 'Own research relevant to LAMS',
        table: {
          head: ['Work', 'Venue', 'Year'],
          rows: [
            ['Complexity in Project Management: An Analysis of Budget Deviation in Brazilian Infrastructure Projects', 'XLII ENANPAD; MundoPM (2023)', '2018'],
            ['The Effect of Project Risk Management on Project Success: A Multidimensional Analysis', 'ENANPAD', '2019'],
            ['Risks and Cost Deviations in Project Management: A Bibliometric Study', 'CONTECSI', '2018'],
            ['Cost Effectiveness in Total Project Cost Maintenance in ERP Implementation Using Fixed-Price Contracts: An Oil & Gas Case Study', 'VI SINGEP', '2017'],
            ['Learning Goal Orientation as a Background to Project Efficiency: The Mediating Role of Impact on the Team', 'European Business Review', '2023'],
          ],
        },
        sources: [{ kind: 'company', label: 'Merlion IT profile — Marcos Mazieri' }],
      },
      {
        title: 'Supervised research relevant to project and public-sector governance',
        table: {
          head: ['Thesis', 'Level and role', 'Year'],
          rows: [
            ['Contracting Modality and Project Cost from the Perspective of Transaction Cost Theory', 'Master\'s — advisor', '2018'],
            ['The Relationship Between Risk Levels and Budget Deviations in Software Development Projects', 'Master\'s — advisor', '2018'],
            ['Understanding the Influence of Project Risk Management on IS/IT Project Success: A Multidimensional Analysis', 'Doctoral — co-advisor', '2018'],
            ['Smart Cities from the Perspective of Technology and Indicators', 'Doctoral — advisor', '2019'],
            ['PMO and Frugal Innovation in the Public Sector', 'Master\'s — advisor', '2022'],
          ],
        },
        sources: [{ kind: 'company', label: 'Merlion IT profile — Marcos Mazieri' }],
      },
    ],
  },

  {
    id: 'TEAM-05',
    chapter: 'team',
    route: '/team/samuel-lemos',
    title: 'Samuel Lemos — Research & Laboratory Manager',
    headline: 'Research and experimentation before technology reaches a client platform.',
    supporting:
      'Samuel Lemos leads innovation and research at Merlion IT: an electrical and telecommunications engineer, postgraduate in systems analysis, and a master\'s researcher in AI-based optimization and systems diagnosis.',
    visual: 'profile-samuel',
    whyItMatters:
      'LAMS relies on AI by design, event-driven automation and containerized deployment. Merlion IT\'s research function is where approaches of this kind — models, retrieval patterns, automation and infrastructure — are explored and tested, so project teams adopt proven patterns rather than experiments.',
    sourceRefs: [
      { kind: 'company', label: 'Merlion IT profile — Samuel Lemos' },
    ],
    presenterNotes: [
      'Lead with formation and technical depth: two engineering degrees from CEFET-RJ, postgraduate study in systems analysis and renewable energy, and a master\'s in progress at UFPR on AI-based metaheuristics for system optimization and diagnosis.',
      'Name the technology families, not tools one by one: applied AI and retrieval, cloud and containers, automation and integration, data, and telecommunications.',
      'Tie it to LAMS lightly: these are the same families of technology behind AI by design, the event layer and containerized deployment.',
      'Company-level research and innovation for all Merlion IT engagements — not a LAMS delivery role.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Education',
        bullets: [
          'Master\'s in Optimization and Diagnosis of Systems using Metaheuristics with Artificial Intelligence — Universidade Federal do Paraná (UFPR), in progress.',
          'Postgraduate degree in Systems Analysis — UniBF.',
          'Postgraduate degree in Renewable Energy — UNINTER.',
          'Data Science with emphasis on Financial Markets — IA Expert / XP, in progress.',
          'Industrial Electrical Engineering with emphasis on Telecommunications — CEFET-RJ.',
          'Telecommunications Engineering — CEFET-RJ.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Samuel Lemos' }],
      },
      {
        title: 'Technical knowledge',
        table: {
          head: ['Area', 'Knowledge'],
          rows: [
            ['AI and data', 'LLM APIs and realtime voice models, retrieval-augmented generation (RAG), LangChain, FAISS vector search, TensorFlow, Keras, scikit-learn, Pandas, NumPy.'],
            ['Cloud and DevOps', 'AWS (EC2, S3, load balancing, ACM, SSM), Docker and Docker Compose, Nginx, Traefik, Portainer, Linux and Windows Server, GitHub Actions, CI/CD.'],
            ['Development', 'Python, JavaScript, SQL, Shell, FastAPI, Flask, Streamlit, React, Vite, Pydantic, Selenium.'],
            ['Data', 'PostgreSQL, Redis, MySQL, SQLite, Supabase; relational modeling, migrations, caching and event persistence.'],
            ['Automation and integration', 'n8n, CRM platforms, webhooks, REST APIs and OAuth 2.0.'],
            ['Telecommunications', 'Asterisk, FreePBX, SIP / PJSIP, ARI / AMI, Twilio, VoIP, RF, fiber optics, TDM and PABX.'],
            ['Engineering and automation', 'KNX building and industrial automation, energy efficiency, electronic security, CFTV, access control, AutoCAD and MS Project.'],
          ],
        },
        sources: [{ kind: 'company', label: 'Merlion IT profile — Samuel Lemos' }],
      },
      {
        title: 'Engineering for critical environments',
        body: [
          'Participation in industrial, infrastructure and security projects in high-complexity environments, including offshore platforms and FPSOs, refineries, sensitive installations, automatic detection systems for tunnels and manufacturing facilities.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Samuel Lemos' }],
      },
      {
        title: 'Certifications, courses and languages',
        bullets: [
          'KNX certification — international automation protocol.',
          'Bosch certification — security systems.',
          'AI Build; ChatGPT for Data Science and Machine Learning; TensorFlow 2.0 — Machine Learning and Data Science (IA Expert Academy); AutoCAD.',
          'Portuguese (native); English (advanced).',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Samuel Lemos' }],
      },
    ],
  },

  {
    id: 'TEAM-06',
    chapter: 'team',
    route: '/team/dylan-marlowe',
    title: 'Dylan Marlowe — Customer Experience & Sales',
    headline: 'Someone who has done condition assessment in the field.',
    supporting:
      'Customer Experience & Sales Head at Merlion IT. Market experience operating AI-driven inspection technology on wastewater and underground infrastructure, now applied to how Merlion evolves its products: customer-success validation, solution improvement, market analysis and positioning.',
    visual: 'profile-dylan',
    whyItMatters:
      'Field condition assessment is where asset management stops being theory. Having someone who has done that work keeps the customer\'s experience — not the vendor\'s assumptions — driving how the product is shaped and explained.',
    sourceRefs: [
      { kind: 'company', label: 'Merlion IT profile — Dylan Marlowe' },
    ],
    presenterNotes: [
      'Introduce by relevance, not by sales record: he is the person at Merlion who has operated inspection robots on buried infrastructure and assessed the condition of physical assets in the field.',
      'Then the role at Merlion: customer experience. He validates customer success against what the product actually does, carries improvements back into the roadmap, and keeps market analysis and positioning grounded in how the work is really done.',
      'That is the same discipline applied to LAMS: the product evolves from what users hit in practice, not from what a vendor assumes.',
      'Say the certifications carefully: NASSCO PACP, MACP and LACP, with recertification pending. Do not present them as current.',
      'Company-level support, like everyone in this section — not a named LAMS delivery role.',
    ],
    estimatedSeconds: 60,
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Market experience',
        bullets: [
          'Operated, maintained and repaired a fleet of autonomous inspection robots, keeping them available for critical infrastructure assessment.',
          'Participated in the development of software that streamlines field work for wastewater infrastructure inspection.',
          'Supported engineering in the design of robotic hardware and software, providing field-level feedback on usability and reliability.',
          'Hands-on knowledge of AI-driven software, autonomous robotics and the reporting and coding tools used in underground infrastructure inspection.',
          'Team leadership and coaching, with a record of managing sales teams against targets.',
          'NASSCO PACP, MACP and LACP certified; recertification pending.',
        ],
        sources: [{ kind: 'company', label: 'Merlion IT profile — Dylan Marlowe' }],
      },
      {
        title: 'How the role works at Merlion IT',
        table: {
          head: ['Front', 'What it means in practice'],
          rows: [
            ['Product evolution', 'Operational needs seen in the field are turned into product feedback and carried into the roadmap.'],
            ['Customer-success validation', 'Checks that what the customer set out to achieve is what the product actually delivers, against the way the work is really done.'],
            ['Solution improvement', 'Usability, reliability and workflow gaps are identified and prioritized with engineering rather than reported and left.'],
            ['Market analysis', 'Reads the market from the operational side — the problems customers have, not only the features competitors announce.'],
            ['Positioning', 'Translates technical capability — AI, autonomous robotics, infrastructure inspection — into value a customer recognizes, and supports business development for AI-based software.'],
          ],
        },
        sources: [{ kind: 'company', label: 'Merlion IT profile — Dylan Marlowe' }],
      },
    ],
  },

  {
    id: 'TEAM-07',
    chapter: 'team',
    route: '/team/core-team',
    title: 'Team',
    headline: 'Chosen for LAMS: architecture, land and GIS expertise, and delivery experience.',
    supporting:
      'Five named professionals are specifically assigned to key delivery roles. Specialist roles are staffed from the same senior bench.',
    visual: 'team-grid',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Sections 7.1-7.3' },
      { kind: 'cv', label: 'Submitted CVs' },
    ],
    presenterNotes: [
      'Now the project staff the RFP asked for: five named professionals, each assigned to a specific LAMS role. This is the team in the submitted proposal, unchanged.',
      'Introduce by relevance to LAMS, not by résumé. One line each.',
      'The RFP asks for the assigned project manager and staff with their knowledge, skills, certifications and experience with similar land applications. Answer that directly.',
      'Do not read biographies. They are behind Explore Details for evaluators who want them.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: ['Staff Qualifications — assigned Project Manager and staff'],
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Named key personnel',
        table: {
          head: ['Role', 'Named professional', 'Relevance to LAMS', 'Involvement'],
          rows: [
            ['Software Architect / Technical Lead', 'Jesse Rovira', '20+ years in software development, solution architecture, enterprise integration, workflow systems and data architecture. Architecture and technical management for the PGA national platform; IBM Maximo-based government solutions.', 'Project-long'],
            ['Project Manager / Scrum Master', 'Felipe Nonato Marinho', '15+ years delivery, 9+ years Agile/Scrum, public-sector digital transformation, Scrum.org certified. Documented Scrum-based government implementations using IBM Maximo / SmartCloud Control Desk.', 'Project-long'],
            ['Fullstack Developer Sr.', 'Pedro Bueno Bucci Bamberg de Araujo', '9+ years engineering, Tech Lead at Merlion IT. React/TypeScript, Node.js/TypeScript, SQL Server, REST/SOAP, Docker, CI/CD. ~8 years on CNA agricultural traceability systems.', 'Primary development lifecycle'],
            ['GIS Analyst Sr.', 'Carlos Frederico Dias de Alencar Ribeiro', 'Agronomist, 20+ years across geoprocessing and spatial analysis, rural-property georeferencing, land regularization and environmental licensing. ArcGIS and QGIS.', 'Discovery through system validation'],
            ['UX/UI Designer Sr.', 'Bruno Freitas', '16+ years IT, recent years in modern web engineering and interface delivery. React, Next.js, TypeScript, Material UI, design systems across public-sector and enterprise systems.', 'Discovery and functional delivery'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Proposal', locator: 'Sections 7.2, 7.3' }],
      },
      {
        title: 'Certifications and qualifications',
        bullets: [
          'Felipe Nonato Marinho — Scrum Master certification, Scrum.org.',
          'Bruno Freitas — IBM Certified Deployment Professional, Maximo Asset Management V7.1 (06/2011); Maximo Application Suite Sales Foundation (04/2023); Maximo Visual Inspection Technical Sales Intermediate (04/2024); Maximo Application Suite Technical Sales Intermediate (12/2024).',
          'Carlos Ribeiro — MBA in Project Management (ESALQ/USP); postgraduate specializations in Rural Property Georeferencing and in Geoprocessing; B.Sc. Agronomy, Universidade de Brasília.',
        ],
        sources: [{ kind: 'cv', label: 'Submitted CVs' }],
      },
      {
        title: 'Other specialist roles',
        body: [
          'Requirements Analyst Sr., Infrastructure Analyst Sr., Data Migration / ETL Specialist Sr., QA / Test Engineer Sr., and Training / Documentation Specialist are staffed with professionals holding seniority, technical qualifications and experience appropriate to their defined responsibilities. Specialist assignments may vary by project phase without reducing the qualifications or delivery capacity represented in the proposal.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposal', locator: 'Section 7.2' }],
      },
    ],
  },

  {
    id: 'TEAM-08',
    chapter: 'team',
    route: '/team/alignment',
    title: 'Team Alignment to the Project',
    headline: 'Every responsibility area of the project has a named owner.',
    supporting:
      'The delivery organization maps four responsibility areas to named professionals and senior specialist roles, each with a planned involvement across the 34-week program.',
    visual: 'team-alignment',
    whyItMatters:
      'Before the project starts, the District can see who is accountable for governance, architecture and engineering, GIS and data, and quality and readiness — and in which phases each role is engaged.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Sections 7.1, 7.2' },
    ],
    presenterNotes: [
      'Walk the four responsibility areas once, left to right. The point is coverage: there is no area of the project without an owner.',
      'Named professionals own the core of each area — Felipe for governance, Jesse and Pedro for architecture and engineering, Carlos for GIS, Bruno for experience. Senior specialists complete each area from the same bench.',
      'Involvement is phase-aligned rather than flat: the Project Manager and Software Architect are project-long; GIS runs from discovery through system validation; data migration from discovery through migration acceptance; QA from development through go-live.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Staff Qualifications — assigned Project Manager and staff'],
    evalTags: ['EVAL-STAFF', 'EVAL-WP'],
    exploreDetails: [
      {
        title: 'Roles, responsibilities and planned involvement',
        table: {
          head: ['Role', 'Named professional', 'Primary responsibilities', 'Planned involvement'],
          rows: [
            ['Project Manager / Scrum Master', 'Felipe Nonato Marinho', 'Project governance, sprint coordination, schedule, reporting, risk and issue management, stakeholder coordination, backlog execution and acceptance tracking.', 'Project-long'],
            ['Software Architect / Technical Lead', 'Jesse Rovira', 'Solution architecture, technical leadership, application and data architecture, integration strategy, security architecture, AI architecture, technical review and release quality.', 'Project-long'],
            ['Fullstack Developer Sr.', 'Pedro Bucci', 'React/TypeScript frontend, Node.js/TypeScript backend, application services, workflows, APIs, integrations, reporting and automated tests.', 'Primary development lifecycle'],
            ['GIS Analyst Sr.', 'Carlos Ribeiro', 'ArcGIS analysis and integration, GIS services and layer validation, mapping workflows, acquisition/disposition triage and land-management GIS context.', 'Discovery through system validation'],
            ['Infrastructure Analyst Sr.', 'Senior specialist', 'District-hosted/container environments, connectivity, deployment, security configuration, monitoring, release promotion and production readiness.', 'Foundation, releases and go-live'],
            ['Requirements Analyst Sr.', 'Senior specialist', 'Discovery, process analysis, backlog refinement, business rules, user stories, acceptance criteria, traceability and UAT support.', 'Discovery through UAT'],
            ['Data Migration / ETL Specialist Sr.', 'Senior specialist', 'Source profiling, mapping, migration, transformation, reconciliation, exception handling and migration evidence.', 'Discovery through migration acceptance'],
            ['QA / Test Engineer Sr.', 'Senior specialist', 'Test strategy, functional/integration/regression testing, defect management, release validation, UAT preparation and acceptance evidence.', 'Development through go-live'],
            ['UX/UI Designer Sr.', 'Bruno Freitas', 'Interaction design, responsive usability, workflow-oriented screen design, prototypes and UX validation.', 'Discovery and functional delivery'],
            ['Training / Documentation Specialist', 'Senior specialist', 'User documentation, Quick Steps, training materials, operational documentation, training support and knowledge transfer.', 'Progressive documentation and final readiness'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Section 7.2' }],
      },
    ],
  },

  {
    id: 'TEAM-09',
    chapter: 'team',
    route: '/team/domain-expertise',
    title: 'Domain Expertise',
    headline: 'The team combines software architecture with land and GIS knowledge.',
    supporting:
      'Land regularization, rural property georeferencing and environmental licensing are not adjacent skills for this project. They are the subject matter.',
    visual: 'expertise-matrix',
    whyItMatters:
      'A GIS analyst who is also an agronomist with two decades of land-regularization practice reads the District\'s Section 5.C program list as familiar work, not as a glossary to learn.',
    sourceRefs: [
      { kind: 'cv', label: 'Submitted CVs' },
      { kind: 'proposal', label: 'Merlion Proposal', locator: 'Section 7.3' },
    ],
    presenterNotes: [
      'Carlos is the strongest card in this chapter for a land-management audience. Give him the most time.',
      'Connect his background to the District\'s actual needs: parcel and boundary mapping, georeferencing of rural properties, environmental assessment.',
      'Jesse covers enterprise architecture and EAM; Pedro covers the engineering stack the solution is built on.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Staff Qualifications — experience with similar land applications'],
    evalTags: ['EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Carlos Frederico Dias de Alencar Ribeiro — GIS Analyst Sr.',
        bullets: [
          'Agronomist with over 20 years spanning geoprocessing and spatial data analysis, land regularization, environmental licensing and public-sector program coordination.',
          'Partner at ArqMap since 2014 — environmental consulting, land regularization and geoprocessing services, including topographic surveys, georeferencing of rural properties, and parcel and boundary mapping using ArcGIS and QGIS.',
          'Technical and Administrative Coordinator at Instituto CNA (2016-2023), coordinating national agricultural technology platforms including AgriTrace Vegetal and AgriTrace Animal.',
          'Natural Resources and Environmental Analyst at CENSIPAM (2006-2010) — technical studies, research and diagnostics including spatial data analysis across the Legal Amazon.',
          'Advisor, Environmental Licensing Directorate at SEMARH (2005-2006) — technical opinions for licensing of infrastructure works, land subdivision, mineral extraction and waste treatment.',
          'Geoprocessing Professor at Faculdade CNA since 2020.',
        ],
        sources: [{ kind: 'cv', label: 'Submitted CV — Carlos Ribeiro' }],
      },
      {
        title: 'Expertise coverage across the named team',
        table: {
          head: ['Domain', 'Coverage'],
          rows: [
            ['Software platform architecture', 'Jesse Rovira — 20+ years solution architecture, enterprise integration, workflow systems, data architecture.'],
            ['Public sector', 'Jesse (Ministry of Tourism, DATASUS, DNIT, MAPA); Felipe (RNP, PAHO, Fiocruz); Carlos (CENSIPAM, SEMARH, Instituto CNA); Bruno (Fiocruz / DF health secretariat).'],
            ['GIS and spatial analysis', 'Carlos Ribeiro — ArcGIS, QGIS, working knowledge of PostGIS; postgraduate specializations in Geoprocessing and Rural Property Georeferencing.'],
            ['Land domain', 'Carlos Ribeiro — land regularization, rural property georeferencing, parcel and boundary mapping, environmental licensing.'],
            ['Enterprise Asset Management', 'Jesse Rovira (ID2 / Magna, IBM Innovation award); Bruno Freitas (Maximo V7.1, Control Desk 7.6, certified); Felipe Nonato Marinho (Maximo / SmartCloud Control Desk government implementations).'],
            ['Workflow and traceability systems', 'Jesse, Pedro and Carlos — national animal and plant traceability platforms with multi-organization workflows.'],
            ['Data migration', 'Pedro Bucci — legacy modernization and system integration; dedicated Data Migration / ETL Specialist Sr. on the delivery team.'],
            ['AI', 'Jesse Rovira — documented AI integration case study work; AI architecture responsibility on LAMS.'],
          ],
        },
        sources: [
          { kind: 'cv', label: 'Submitted CVs' },
          { kind: 'proposal', label: 'Merlion Proposal', locator: 'Sections 7.2, 7.3' },
        ],
      },
    ],
  },

  {
    id: 'TEAM-10',
    chapter: 'team',
    route: '/team/references',
    title: 'Relevant References',
    headline: 'Experience delivering complex, workflow-driven platforms.',
    supporting:
      'Four of the five named professionals have worked together inside the CNA and Instituto CNA agricultural technology ecosystem on national-scale traceability and integration programs.',
    visual: 'reference-cards',
    sourceRefs: [
      { kind: 'proposal', label: 'Submitted Form B — Project References' },
      { kind: 'proposal', label: 'Merlion Proposal, Relevant Experience', locator: 'Sections 7.3, 7.4' },
    ],
    presenterNotes: [
      'The RFP asks specifically for the number of similar projects the staff have worked together on, and their status — with on-time and on-budget outcomes cited as the example format.',
      'Answer it honestly, including the variances. Our proposal states that we do not invent planned-versus-actual metrics where verified source records are not available, and that candour is worth more here than a clean but unsupported table.',
      'PGA is the strongest analogue to LAMS: a national platform coordinating 27 federative units over roughly six years, through changing public policy and budget cycles. It is also the one with schedule variance. Present both facts together.',
      'AgriTrace Animal shows the architecture absorbing major scope expansion — from cattle to swine and equines — without redesign. That is directly relevant to a system the District expects to evolve.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: [
      'Staff Qualifications — number of similar projects staff have worked together on as a team and status',
      'Time and Cost — planned and accomplished historical timeframes for similar projects',
    ],
    evalTags: ['EVAL-STAFF', 'EVAL-TC'],
    exploreDetails: [
      {
        title: 'Team delivery record',
        table: {
          head: ['Project', 'Status'],
          rows: [
            ['AgriTrace Vegetal — CNA / Instituto CNA', 'Completed on time and within budget. A national-scale plant traceability platform supporting distributed agricultural stakeholders, traceability records, compliance-oriented workflows and operational documentation.'],
            ['Agricultural Management Platform (PGA) — CNA / MAPA', 'Completed with schedule variance. The most complex initiative undertaken jointly by members of the proposed team: coordination with Brazil\'s 27 federative units, evolving over approximately six years, affected by multiple stakeholder environments, changes in public policy and regulatory priorities, government decision cycles and public-sector budget constraints.'],
            ['AgriTrace Animal — CNA / Instituto CNA', 'Completed with material scope evolution. Initially structured around cattle traceability, subsequently expanded to additional species including swine and equines. The core technical architecture supported this evolution; business rules, documentation, manuals and operational scenarios required adaptation.'],
            ['Avenyx.AI — United States', 'Currently in delivery. A U.S.-based international education operations platform including CRM, workflow, applicant and agency onboarding, institutional coordination, integrations and ongoing product evolution.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Proposal', locator: 'Section 7.4' }],
      },
      {
        title: 'Why this record is relevant to LAMS',
        body: [
          'These initiatives illustrate experience operating where delivery conditions evolve over time. PGA required sustained coordination among numerous public-sector stakeholders and adaptation to policy and funding conditions. AgriTrace Animal required the underlying solution to accommodate substantial domain expansion after development had begun.',
          'That experience is the reason Sprint 0, backlog refinement, formal change control, modular architecture and continuous stakeholder engagement are built into this plan rather than added to it.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposal', locator: 'Section 7.4' }],
      },
      {
        title: 'U.S. product development',
        body: [
          'In the United States, Merlion IT\'s delivery model is increasingly centred on the conception, architecture, development and operation of software products rather than exclusively on third-party project execution. This includes Avenyx.AI and JobFlow AI, a proprietary product under development for the U.S. construction industry.',
          'This product-oriented practice demonstrates the ability to translate evolving business needs into maintainable software, define architecture and roadmaps, deliver iteratively, and remain responsible for long-term evolution beyond an initial implementation.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposal', locator: 'Section 7.3' }],
      },
    ],
  },

  {
    id: 'TEAM-11',
    chapter: 'team',
    route: '/team/eam-expertise',
    title: 'Our EAM Expertise',
    headline: 'This design is grounded in practical EAM experience.',
    supporting:
      'We are not borrowing EAM terminology. We are applying a management discipline our team has implemented and extended through enterprise platforms such as IBM Maximo.',
    visual: 'eam-expertise',
    whyItMatters:
      'Three of the five named professionals on this project have documented IBM Maximo experience — including the Software Architect who is responsible for the LAMS architecture itself.',
    sourceRefs: [
      { kind: 'cv', label: 'Submitted CV — Jesse Rovira' },
      { kind: 'cv', label: 'Submitted CV — Bruno Freitas' },
      { kind: 'proposal', label: 'Merlion Proposal, Project Team', locator: 'Sections 7.2, 7.3' },
    ],
    presenterNotes: [
      'Lead with Jesse. He is the Software Architect and Technical Lead on this project, so his EAM background is directly the thing shaping the design.',
      'The IBM Innovation award is the strongest single data point: it was earned in 2010 for applying Maximo — a platform designed for physical assets — to manage biological elements and their information chains. That is exactly the intellectual move we are making again with land.',
      'Be careful and explicit: IBM does not sponsor, certify or endorse LAMS. We are describing our people\'s experience, not a product relationship.',
      'Only cite what is in the submitted resumes. Do not embellish.',
      'This scene closes the team section. End on the idea, not the credential: the team has applied this discipline for years — and it is the lens through which we read the District\'s challenge next.',
    ],
    estimatedSeconds: 90,
    evalTags: ['EVAL-STAFF', 'EVAL-WP'],
    exploreDetails: [
      {
        title: 'Jesse Rovira — Software Architect / Technical Lead on LAMS',
        bullets: [
          'Founded ID2 Tecnologia in Brasília on the strategic concept that "Everything is an Asset," using the IBM Maximo platform to manage physical, financial, human and informational assets.',
          'Led technical teams designing and implementing Maximo-based solutions for government and institutional clients including the Ministry of Tourism, DATASUS and DNIT.',
          'Received IBM\'s "Innovation" award in 2010 in recognition of more than ten successful solution deployments using IBM Maximo technologies — specifically for applying a platform designed for physical assets to biological elements and their associated information chains.',
          'Lecturer at IBM\'s Pulse conference in Las Vegas, February 2011, presenting the use of IBM Maximo with the concept of "Everything is ACTIVE!" in the asset-management innovation cycle.',
          'Following Magna Sistemas\' acquisition of the ID2 project portfolio, led commercial development and strategic positioning of Enterprise Asset Management solutions.',
        ],
        sources: [{ kind: 'cv', label: 'Submitted CV — Jesse Rovira' }],
      },
      {
        title: 'Bruno Freitas — UX/UI Designer Sr. on LAMS',
        bullets: [
          'Professional experience as an IBM Maximo and IBM Control Desk developer, including IBM Control Desk 7.6.',
          'IBM Maximo Asset Management V7.1 installation and configuration, configuration and customization to business demands, and web-service integration using WSDL/SOAP.',
          'Delivered training sessions for users and technical teams on IBM Maximo Asset Management V7.1.',
          'IBM Certified Deployment Professional — Maximo Asset Management V7.1 (06/2011).',
          'Maximo Application Suite Sales Foundation (04/2023); Maximo Visual Inspection Technical Sales Intermediate (04/2024); Maximo Application Suite Technical Sales Intermediate (12/2024).',
        ],
        body: [
          'Bruno is assigned to LAMS as UX/UI Designer Sr., where his focus is interaction consistency, responsive usability and design-system application. His EAM background is listed here as evidence of the team\'s collective familiarity with the discipline, not as his project role.',
        ],
        sources: [
          { kind: 'cv', label: 'Submitted CV — Bruno Freitas' },
          { kind: 'proposal', label: 'Merlion Proposal', locator: 'Sections 7.2, 7.3' },
        ],
      },
      {
        title: 'Felipe Nonato Marinho — Project Manager / Scrum Master on LAMS',
        bullets: [
          'Documented experience with Scrum-based government implementations using IBM Maximo and IBM SmartCloud Control Desk.',
          'More than 15 years of technology project delivery and more than 9 years applying Agile and Scrum methods.',
          'Scrum Master certification from Scrum.org.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Proposal', locator: 'Sections 7.3, 7.4' }],
      },
      {
        title: 'What we are not claiming',
        bullets: [
          'LAMS is not IBM Maximo and contains no IBM Maximo code or licensing.',
          'IBM does not sponsor, certify or endorse LAMS.',
          'The certifications above belong to individuals and are current as recorded in the submitted resumes.',
        ],
      },
    ],
  },
]
